import React, { useContext, useEffect, useState, useMemo } from 'react';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { UserContext } from '../context/UserContext';
import { getCommunityIdentityLoginUrl } from '@atlassian/community-library';
import { getUserAccountStatuses } from '../services/http/ChampionGraphQlOperations';
import { getEnvironmentAsEnum } from '../utils/environment';
import { PageWithAnalytics } from '../components/common/analytics-wrapped/PageWithAnalytics';
import DocumentHead from '../components/common/DocumentHead';
import { DEFAULT_CHAMPIONS_SIGNUP_URL } from '../utils/constants';
import { championsAuthSignupIsActive } from '../services/statsig/featureGates';
import ChampionConsentForm from '../components/champions/ChampionConsentForm';
import { getEntryPointFromUrl } from '../analytics/champions-helpers';

const TITLE = 'Champions Consent | Atlassian Community';
const DESCRIPTION = 'Champions consent page.';
const CHAMPIONS_CONSENT_PATH = '/champions/consent';

const getConsentRedirectUrl = () => `${process.env.HOME_BASE_URL}${CHAMPIONS_CONSENT_PATH}`;

type AccountInfo = {
  emailId: string;
  isManaged: boolean;
};

export const ChampionsConsentPage: React.FC = () => {
  const { user, userIsInitialized } = useContext(UserContext) ?? {};
  const isLoggedIn = !!user;
  const isAuthSignupEnabled = championsAuthSignupIsActive();
  const [accountInfo, setAccountInfo] = useState<AccountInfo | null>(null);
  const [accountError, setAccountError] = useState(false);
  const [accountLoading, setAccountLoading] = useState(true);
  const homepage = process.env.REACT_APP_ENVI === 'local' ? 'http://localhost:9000' : process.env.HOME_BASE_URL;

  const entryPoint = useMemo(() => getEntryPointFromUrl(), []);
  const userId = user?.account_id;

  const screenAttributes = useMemo(() => ({
    entryPoint,
    referrer: document.referrer || 'none',
    featureGateEnabled: isAuthSignupEnabled,
    pagePath: CHAMPIONS_CONSENT_PATH,
    isAuthenticated: !!userId,
    userId: userId || 'anonymous',
  }), [entryPoint, isAuthSignupEnabled, userId]);

  useEffect(() => {
    if (!isAuthSignupEnabled) {
      window.location.href = DEFAULT_CHAMPIONS_SIGNUP_URL;
      return;
    }
    if (!userIsInitialized || !isLoggedIn) {
      window.location.href = getCommunityIdentityLoginUrl(
        { redirectUrl: getConsentRedirectUrl(), experience: 'champion-consent' },
        getEnvironmentAsEnum(),
      );
      return;
    }
    if (!user?.account_id) {
      setAccountError(true);
      setAccountLoading(false);
      return;
    }
    let cancelled = false;
    const fetchAccountInfo = async () => {
      try {
        const response = await getUserAccountStatuses(user.account_id, homepage, homepage);
        const account = response?.data?.unified?.account;
        if (cancelled || !account || 'code' in account || !account.emailId) {
          if (!cancelled) {
            setAccountError(true);
            setAccountLoading(false);
          }
          return;
        }

        setAccountInfo({
          emailId: account.emailId,
          isManaged: account.isManaged === true,
        });
        setAccountLoading(false);
      } catch {
        if (!cancelled) {
          setAccountError(true);
          setAccountLoading(false);
        }
      }
    };
    fetchAccountInfo();
    return () => {
      cancelled = true;
    };
  }, [isAuthSignupEnabled, userIsInitialized, isLoggedIn, user?.account_id, homepage]);

  const canShowContent = isAuthSignupEnabled && userIsInitialized && isLoggedIn;

  if (!canShowContent || accountLoading) {
    return (
      <div css={pageStyles}>
        <div css={containerStyles}>
          <p css={descriptionStyles}>Loading...</p>
        </div>
      </div>
    );
  }

  if (accountError || !accountInfo) {
    return (
      <div css={pageStyles}>
        <div css={containerStyles}>
          <p css={errorBannerStyles}>Oops! Something went wrong.</p>
        </div>
      </div>
    );
  }

  return (
    <PageWithAnalytics eventScreenName="championsConsentScreen" attributes={screenAttributes}>
      <DocumentHead title={TITLE} description={DESCRIPTION} />
      <div css={pageStyles}>
        <div style={{ width: '100%' }}>
          <ChampionConsentForm
            accountId={user.account_id}
            accountIsManaged={accountInfo.isManaged}
            name={user?.name ?? ''}
            emailId={accountInfo.emailId}
            userId={user?.account_id}
            entryPoint={entryPoint}
          />
        </div>
      </div>
    </PageWithAnalytics>
  );
};

export default ChampionsConsentPage;

const containerStyles = css({
  maxWidth: '800px',
  minWidth: '375px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: token('space.600'),
});

const descriptionStyles = css({
  marginBottom: token('space.200'),
  color: token('color.text'),
  fontFamily: 'Inter',
  fontWeight: 400,
  lineHeight: '24px',
  textAlign: 'center',
  fontSize: '18px',
});

const pageStyles = css({
  display: 'flex',
  width: '100%',
  flexDirection: 'column',
  alignItems: 'center',
  paddingTop: token('space.400'),
  paddingBottom: token('space.800'),
  minHeight: '400px',
});

const errorBannerStyles = css({
  color: '#3e3e3c',
  fontSize: '18px',
  fontWeight: 400,
  textAlign: 'center',
  lineHeight: '24px',
});
